<script lang="ts">
	import Repository from './Repository.svelte';
  import { onMount } from "svelte";
  import { appStore } from "../stores/appStore";
  import { getTokenStorage } from "../utils/localStorage";
  import { AppApiClient } from "../api_contracts/app.client";
  import RepositoryHeader from "../components/RepositoryHeader.svelte";
  import RepositoryNav from "../components/RepositoryNav.svelte";
  import FileBrowser from "../components/FileBrowser.svelte";
  import ReadmeViewer from "../components/ReadmeViewer.svelte";
  import EmptyRepositoryHelp from "../components/EmptyRepositoryHelp.svelte";
  import RepositorySidebar from "../components/RepositorySidebar.svelte";
  import IssuesView from "../components/IssuesView.svelte";
  import PullRequestsView from "../components/PullRequestsView.svelte";
  import SecurityView from "../components/SecurityView.svelte";
  import SettingsView from "../components/SettingsView.svelte";
  import Navbar from "../components/Navbar.svelte";
  import { UserProfile } from "../api_contracts/app";
  import { RepositoryData } from "../models/state";
 

  // State
  let client: AppApiClient | null = $state(null);
  let profile: UserProfile | null = $state(null);
  let token: string = $state("");
  let repoName = $state("");
  // let owner = $state('');
  let activeTab = $state("code");
  let loading = $state(true);
  let error = $state("");

  // Repository data - will be loaded from backend
  let repository: RepositoryData | null = $state(null);

  // Subscribe to store
  appStore.subscribe((state) => {
    profile = state.profile;
    client = state.client;
    token = state.token;
  });

  const handleTabChange = (tab: string) => {
    activeTab = tab;
  };

  async function fetchUserProfile() {
    if (!client ) {
      error = "Client not initialized";
      loading = false;
      return false;
    }

    if ( !token) {
      // error = "Not authenticated";
      loading = false;
      return false;
    }


    try {
      const result = await client.getUserProfile({ token });

      if (result.response.response.successFull) {
        profile = result.response.profile;
        return true;
      } else {
        error =
          result.response.response.reason || "Failed to fetch user profile.";
        return false;
      }
    } catch (e: any) {
		console.log("here"+e);
      error = e.message || "Error fetching user profile";
      console.error("Error fetching user profile:", e);
      return false;
    }
  }

  async function fetchRepositoryDetails() {
    if (!client) {
      error = "Failed to initialize client";
      loading = false;
      return;
    }

    try {
      loading = true;
      error = "";

      // Extract username from URL: /users/:username/repositories/:name
      const hash = window.location.hash;
      const ownerMatch = hash.match(/\/users\/([^\/]+)\/repositories/);

      if (!ownerMatch) {
        error = "Invalid repository URL";
        loading = false;
        return;
      }

      const ownerIdOrUsername = ownerMatch[1];

      const result = await client.getRepositoryDetails({
        token: token || "",
        ownerId: ownerIdOrUsername,
        repositoryName: repoName,
      });

      console.log("Repository details result:", result.response);
      if (result.response.successFull && result.response.repository) {
        const repo = result.response.repository;
        const ownerData = result.response.owner;

        repository = {
          id: repo.id,
          ownerId: repo.ownerId,
          name: repo.name,
          visibility: repo.visibility as "public" | "private",
          watchCount: repo.watchCount,
          starCount: repo.starCount,
          forkCount: repo.forkCount,
          description: repo.description || "",
          website: repo.website || "",
          stars: repo.starCount,
          issueCount: repo.issueCount,
          prCount: repo.prCount,
          branch: repo.defaultBranch,
          avatarUrl: repo.avatarUrl || null,
          owner: {
            id: ownerData.id,
            userName: ownerData.userName,
            avatarUrl: ownerData.avatarUrl,
          },
          lastCommit: result.response.lastCommit
            ? {
                message: result.response.lastCommit.message,
                author: result.response.lastCommit.author,
                time: result.response.lastCommit.time,
                sha: result.response.lastCommit.sha,
              }
            : {
                message: "Initial commit",
                author: ownerData.userName,
                time: "just now",
                sha: "a1b2c3d",
              },
          files: [], // Will be filled by fetchRepositoryFiles
          readme: result.response.readme || "",
          languages: result.response.languages
            ? result.response.languages.map((lang) => ({
                name: lang.name,
                percentage: lang.percentage,
                color: lang.color,
              }))
            : [],
        };

        // Fetch files after getting repository details
        await fetchRepositoryFiles();
      } else {
        error = result.response.reason || "Failed to fetch repository details";
      }
    } catch (e: any) {
      error = e.message || "Error fetching repository details";
      console.error("Error fetching repository details:", e);
    } finally {
      loading = false;
    }
  }

  async function fetchRepositoryFiles() {
    if (!client) {
      return;
    }

    try {
      // Extract username from URL: /users/:username/repositories/:name
      const hash = window.location.hash;
      const ownerMatch = hash.match(/\/users\/([^\/]+)\/repositories/);

      if (!ownerMatch) {
        return;
      }

      const ownerIdOrUsername = ownerMatch[1];

      const result = await client.getRepositoryFiles({
        token: token || "",
        ownerId: ownerIdOrUsername,
        repositoryName: repoName,
        branch: repository?.branch || "main",
        path: "/",
      });

      if (result.response.successFull && result.response.files) {
        repository.files = result.response.files
        // repository.files = result.response.files.map((file) => ({
        //   name: file.name,
        //   type: file.type as "file" | "folder",
        //   message: file.message,
        //   updatedAt: file.updatedAt,
        // }));
      }
    } catch (e: any) {
      console.error("Error fetching repository files:", e);
    }
  }

  onMount(() => {
    // Extract repo name from hash: /users/:username/repositories/:name
    const hash = window.location.hash;
    const match = hash.match(/\/users\/[^\/]+\/repositories\/([^\/]+)/);
    if (match) {
      repoName = match[1];
    }

    // Check if authenticated
    const storedToken = getTokenStorage();
    if (storedToken) {
      if (!profile) {
        // Fetch user profile first to get owner ID
        fetchUserProfile().then((success) => {
          if (success) {
             loading = false;
            // Then fetch repository data
            fetchRepositoryDetails();
          } else {
            loading = false;
          }
        });
      } else {
         loading = false;
        // Fetch repository data directly
        fetchRepositoryDetails();
      }
    } else {
      // Not authenticated, but try to fetch public repository
      fetchRepositoryDetails();
    }
  });
</script>

<Navbar />

<div
  class="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
>
  <div class="container mx-auto px-4 py-8 max-w-7xl">
    {#if loading}
      <div class="flex items-center justify-center min-h-[60vh]">
        <div class="text-center">
          <div
            class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
          ></div>
          <p class="mt-4 text-gray-600 dark:text-gray-400">
            Loading repository...
          </p>
        </div>
      </div>
    {:else if error}
      <div class="flex items-center justify-center min-h-[60vh]">
        <div class="max-w-md text-center">
          <div class="mb-6">
            <svg
              class="w-20 h-20 mx-auto text-gray-400 dark:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
            Repository Not Found
          </h2>
          <p class="text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          <div class="space-y-3">
            <a
              href="#/"
              class="block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Go to Home
            </a>
            <button
              onclick={() => window.history.back()}
              class="block w-full px-6 py-3 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
            >
              Go Back
            </button>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-500 mt-6">
            Make sure the username and repository name are correct.
          </p>
        </div>
      </div>
    {:else if repository}
      <!-- Repository Header -->
      <RepositoryHeader
        repositoryId={repository.id}
        ownerUsername={repository.owner.userName}
        name={repository.name}
        visibility={repository.visibility}
        watchCount={repository.watchCount}
        starCount={repository.starCount}
        forkCount={repository.forkCount}
        avatarUrl={repository.avatarUrl}
      /> 
 
      <!-- Repository Navigation -->
      <RepositoryNav
        {activeTab}
        issueCount={repository.issueCount}
        prCount={repository.prCount}
        onTabChange={handleTabChange}
      />

      <!-- Main Content -->
      <main class="flex flex-col lg:flex-row gap-6">
        {#if activeTab === "code"}
          <!-- Left Column - Files and README -->
          <div class="w-full lg:w-3/4">
            {#if repository.files.length != 0 }

             <!-- File Browser -->
            <FileBrowser
              branch={repository.branch}
              lastCommit={repository.lastCommit}
              files={repository.files}
              onFileClick={(file) => {
                const hash = window.location.hash;
                const match = hash.match(/\/users\/([^\/]+)\//);
                const username = match ? match[1] : repository.owner.userName;
                window.location.hash = `/users/${username}/repositories/${repository.name}/files/${file.name}`;
              }}
              ownerId={repository.ownerId}
              repositoryName={repository.name}
            />
            {/if}
            



            <!-- README Viewer or Empty Repository Help -->
            {#if repository.files.length === 0 && !repository.readme}
              <!-- Show git help for empty repositories -->
              <EmptyRepositoryHelp
                repositoryName={repository.name}
                ownerUsername={repository.owner.userName}
              />
            {:else if repository.readme}
              <!-- Show README if it exists -->
              <ReadmeViewer content={repository.readme} filename="README.md" />
            {/if}
          </div>

          <!-- Right Column - Sidebar -->
          <div class="w-full lg:w-1/4">
            <div class="sticky top-8">
              <RepositorySidebar
                description={repository.description}
                website={repository.website}
                stars={repository.stars}
                languages={repository.languages}
              />
            </div> 
          </div>
        {:else if activeTab === "issues"}
          <!-- Issues View -->
          <div class="w-full">
            <IssuesView
              repositoryName={repository.name}
              ownerId={repository.owner.id}
              on:countsUpdated={(e) => {
                if (repository) {
                  repository.issueCount = e.detail.totalCount;
                }
              }}
            />
          </div>
        {:else if activeTab === "pulls"}
          <!-- Pull Requests View -->
          <div class="w-full">
            <PullRequestsView
              repositoryName={repository.name}
              ownerId={repository.owner.id}
            />
          </div>
        {:else if activeTab === "security"}
          <!-- Security View -->
          <div class="w-full">
            <SecurityView />
          </div>
        {:else if activeTab === "settings"}
          <!-- Settings View -->
          <div class="w-full">
            {#if repository}
              <SettingsView
                repositoryName={repoName}
                ownerId={repository.ownerId}
                onSettingsUpdate={() => fetchRepositoryDetails()}
              />
            {/if}
          </div>
        {/if}
      </main>
    {/if}
  </div>
</div>